/**
 * =============================================================================
 * WlTenantsTrendChart
 *
 * Evolucao 12 meses: novos tenants vs. churn. Suporta alternancia bar/line
 * com modo controlado externamente (mode/onModeChange).
 *
 * @version 1.4.0
 * RELEASE:
 *   2026-07-21 v1.0.0 - Versao inicial (somente barras).
 *   2026-07-21 v1.1.0 - Toggle bar/line integrado.
 *   2026-07-21 v1.2.0 - Default do toggle passa a ser linha.
 *   2026-07-21 v1.3.0 - Suporte a modo controlado; toggle interno oculto
 *                       quando o pai fornece mode/onModeChange.
 *   2026-07-22 v1.4.0 - Removido CartesianGrid (visual limpo). Paleta
 *                       padronizada com WlRevenueTrendChart (roxo Planos e
 *                       ambar Add-ons). Tooltip com fundo do card e legenda
 *                       iconType square para ficar identico ao MRR trend.
 * =============================================================================
 */
import { useState } from 'react';
import {
  Bar,
  BarChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { WlFinancialTenantPoint } from '@/hooks/wl/useWlFinancialKpis';
import { ChartTypeToggle, ChartTypeMode } from './ChartTypeToggle';

interface Props {
  data: WlFinancialTenantPoint[];
  mode?: ChartTypeMode;
  onModeChange?: (mode: ChartTypeMode) => void;
}

// Paleta padronizada com o grafico de MRR (WlRevenueTrendChart):
// roxo para "Novos" e ambar para "Churn".
const COLOR_NEW = '#8b7cf6';
const COLOR_CHURN = '#f5a524';

/** Novos vs. churn — barras ou linhas. */
export function WlTenantsTrendChart({ data, mode: modeProp, onModeChange }: Props) {
  const [internalMode, setInternalMode] = useState<ChartTypeMode>('line');
  const isControlled = modeProp !== undefined && onModeChange !== undefined;
  const mode: ChartTypeMode = isControlled ? modeProp! : internalMode;

  const tooltipProps = {
    contentStyle: {
      background: 'hsl(var(--card))',
      border: '1px solid hsl(var(--border))',
      borderRadius: 8,
      fontSize: 12,
    },
  };

  return (
    <div className="relative w-full h-64">
      {!isControlled && (
        <div className="absolute right-0 top-0 z-10">
          <ChartTypeToggle value={mode} onChange={setInternalMode} />
        </div>
      )}

      <ResponsiveContainer width="100%" height="100%">
        {mode === 'bar' ? (
          <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fontSize: 11 }} width={30} />
            <Tooltip {...tooltipProps} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="square" />
            <Bar dataKey="new_tenants" name="Novos" fill={COLOR_NEW} radius={[4, 4, 0, 0]} />
            <Bar dataKey="churned_tenants" name="Churn" fill={COLOR_CHURN} radius={[4, 4, 0, 0]} />
          </BarChart>
        ) : (
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fontSize: 11 }} width={30} />
            <Tooltip {...tooltipProps} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="square" />
            <Line
              type="monotone"
              dataKey="new_tenants"
              name="Novos"
              stroke={COLOR_NEW}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="churned_tenants"
              name="Churn"
              stroke={COLOR_CHURN}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
